import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { InventoryRuleActionType } from "@redotech/crdb-enums/inventory/inventory-rule-action-type";
import { InventoryRuleTrigger } from "@redotech/crdb-enums/inventory/inventory-rule-trigger";
import { accountIdFormat } from "@redotech/redo-account-id";
import { t } from "@redotech/redo-i18n";
import { productIdFormat } from "@redotech/redo-product-id";
import * as z from "zod";
import { UNASSIGNABLE_OUTCOMES } from "./outcome";

/** The action types a rule can take; consolidation and flush come later. */
export const INVENTORY_RULE_ACTION_TYPES = [
  InventoryRuleActionType.AssignOutcome,
  InventoryRuleActionType.TransformProduct,
  InventoryRuleActionType.ListOnChannel,
] as const;
export type InventoryRuleRunnableActionType =
  (typeof INVENTORY_RULE_ACTION_TYPES)[number];

/** Separate, explicit modes, as in the processing flow: map never falls back to generate. */
export const INVENTORY_RULE_TRANSFORM_MODES = ["map", "generate"] as const;
export type InventoryRuleTransformMode =
  (typeof INVENTORY_RULE_TRANSFORM_MODES)[number];

export const INVENTORY_RULE_TRANSFORM_MODE_LABELS: Readonly<
  Record<InventoryRuleTransformMode, () => string>
> = {
  map: () => t("Map to a product"),
  generate: () => t("Generate a product"),
};

const assignableOutcomeSchema = z
  .enum(InventoryItemOutcome)
  .refine((outcome) => !UNASSIGNABLE_OUTCOMES.includes(outcome), {
    message: "A rule can't assign this outcome",
  });

/** Each action's config fields; an action-clock `match` is checked against these too. */
const ACTION_CONFIG_FIELDS = {
  [InventoryRuleActionType.AssignOutcome]: { outcome: assignableOutcomeSchema },
  [InventoryRuleActionType.TransformProduct]: {
    mode: z.enum(INVENTORY_RULE_TRANSFORM_MODES),
    /** The account a map step moves the item into. */
    targetAccountId: accountIdFormat.schema,
  },
  [InventoryRuleActionType.ListOnChannel]: { channel: z.string().min(1) },
} satisfies Record<
  InventoryRuleRunnableActionType,
  Record<string, z.ZodType<string>>
>;

export const assignOutcomeActionConfigSchema = z.strictObject({
  outcome: ACTION_CONFIG_FIELDS[InventoryRuleActionType.AssignOutcome].outcome,
});

/** How the rule builds a product: `{title}` and `{sku}` are the source product's. */
const transformGenerationSchema = z.strictObject({
  titleTemplate: z.string().trim().min(1),
  skuTemplate: z.string().trim().min(1),
  /** Hundredths at most, as `product_generation_rule.value_decrement_percentage` stores it. */
  valueDecrementPercentage: z.number().min(0).max(100).multipleOf(0.01),
});

/** One of the rule's own mappings: the product an item of `sourceProductId` becomes. */
const transformMappingSchema = z.strictObject({
  sourceProductId: productIdFormat.schema,
  productId: productIdFormat.schema,
});

const transformProductFields =
  ACTION_CONFIG_FIELDS[InventoryRuleActionType.TransformProduct];
const transformProductActionConfigSchema = z.discriminatedUnion("mode", [
  z.strictObject({
    mode: z.literal("map"),
    targetAccountId: transformProductFields.targetAccountId,
    /** Unset uses the account's product mappings into `targetAccountId`; an unmapped product is left unchanged (`no_mapping`). */
    mappings: z
      .array(transformMappingSchema)
      .min(1)
      .refine(
        (mappings) =>
          new Set(mappings.map((mapping) => mapping.sourceProductId)).size ===
          mappings.length,
        { message: "Map each source product once" },
      )
      .optional(),
  }),
  z.strictObject({
    mode: z.literal("generate"),
    /** Unset uses the connected recommerce account's generation rule, else makes an unchanged copy. */
    generation: transformGenerationSchema.optional(),
  }),
]);

const listOnChannelActionConfigSchema = z.strictObject({
  channel: ACTION_CONFIG_FIELDS[InventoryRuleActionType.ListOnChannel].channel,
});

/** `inventory_rule_version.action_type` and `action`. */
export const inventoryRuleActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(InventoryRuleActionType.AssignOutcome),
    config: assignOutcomeActionConfigSchema,
  }),
  z.object({
    type: z.literal(InventoryRuleActionType.TransformProduct),
    config: transformProductActionConfigSchema,
  }),
  z.object({
    type: z.literal(InventoryRuleActionType.ListOnChannel),
    config: listOnChannelActionConfigSchema,
  }),
]);
export type InventoryRuleAction = z.infer<typeof inventoryRuleActionSchema>;

const scheduledDaysSchema = z.int().min(1).max(3650);

/** Days since the item entered a bin, from the bin clock. */
const inBinTriggerConfigSchema = z.object({
  anchor: z.literal("in_bin"),
  days: scheduledDaysSchema,
});

/**
 * Days since the item's latest current applied action of `action_type`, among
 * those whose config has every `match` entry.
 */
const actionTriggerConfigSchema = z
  .object({
    anchor: z.literal("action"),
    action_type: z.enum(INVENTORY_RULE_ACTION_TYPES),
    match: z.record(z.string(), z.string()).optional(),
    days: scheduledDaysSchema,
  })
  .superRefine((config, context) => {
    const fields: Readonly<Record<string, z.ZodType<string>>> =
      ACTION_CONFIG_FIELDS[config.action_type];
    for (const [key, value] of Object.entries(config.match ?? {})) {
      const field = fields[key];
      if (field === undefined) {
        context.addIssue({
          code: "custom",
          path: ["match", key],
          message: `${config.action_type} has no ${key}`,
        });
      } else if (!field.safeParse(value).success) {
        context.addIssue({
          code: "custom",
          path: ["match", key],
          message: `Invalid ${key} for ${config.action_type}`,
        });
      }
    }
  });

const scheduledTriggerConfigSchema = z.discriminatedUnion("anchor", [
  inBinTriggerConfigSchema,
  actionTriggerConfigSchema,
]);
export type ScheduledTriggerConfig = z.infer<
  typeof scheduledTriggerConfigSchema
>;

/** `inventory_rule_version.trigger` and `trigger_config`. */
export const inventoryRuleTriggerSchema = z.discriminatedUnion("trigger", [
  z.object({
    trigger: z.literal(InventoryRuleTrigger.Processed),
    config: z.null(),
  }),
  z.object({
    trigger: z.literal(InventoryRuleTrigger.Scheduled),
    config: scheduledTriggerConfigSchema,
  }),
]);
export type InventoryRuleTriggerDefinition = z.infer<
  typeof inventoryRuleTriggerSchema
>;
